// DnsFilterProfiles.tsx
import { useState, useCallback, useEffect } from "react";
import {
    RefreshCw, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, Shield, Filter
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface FtgdFilter {
    id: number;
    log: string;
    action: string;
    category: number;
    q_origin_key: number;
}

interface DnsProfile {
    name: string;
    comment: string;
    "ftgd-dns": { filters: FtgdFilter[]; options: string };
    "strip-ech": string;
    "safe-search": string;
    "block-action": string;
    "block-botnet": string;
    "log-all-domain": string;
    "redirect-portal": string;
    "redirect-portal6": string;
    "sdns-domain-log": string;
    "youtube-restrict": string;
    "sdns-ftgd-err-log": string;
}

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | number | undefined }) => {
    const str = String(val ?? "");
    if (!str || str === "" || str === "0.0.0.0" || str === "::")
        return <span className="text-gray-200 text-[9px]">—</span>;
    const lower = str.toLowerCase();
    const style =
        lower === "enable" || lower === "block" || lower === "redirect"
            ? "bg-green-500/10 border-green-500/20 text-green-400"
            : lower === "disable"
                ? "bg-gray-700/40 border-gray-600/30 text-gray-200"
                : lower === "monitor"
                    ? "bg-cyan-500/10 border-cyan-500/20 text-cyan-400"
                    : lower === "strict"
                        ? "bg-orange-500/10 border-orange-500/20 text-orange-400"
                        : lower === "moderate"
                            ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
                            : "bg-indigo-500/10 border-indigo-500/20 text-indigo-400";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold border uppercase whitespace-nowrap ${style}`}>
            {str}
        </span>
    );
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ col, sort }: { col: string; sort: { key: string; dir: "asc" | "desc" } }) => {
    if (sort.key !== col) return <ArrowUp size={9} className="opacity-0 group-hover:opacity-30 text-gray-200 transition-opacity" />;
    return sort.dir === "asc"
        ? <ArrowUp size={9} className="text-teal-400" />
        : <ArrowDown size={9} className="text-teal-400" />;
};

// ── Table Columns ─────────────────────────────────────────────────────────────
const COLS: { key: string; label: string; width: string; render: (p: DnsProfile) => React.ReactNode }[] = [
    {
        key: "name", label: "Profile Name", width: "min-w-[160px]",
        render: p => (
            <div>
                <p className="text-[11px] font-black text-white">{p.name}</p>
            </div>
        ),
    },
    {
        key: "_filterCount", label: "Filters", width: "w-16",
        render: p => (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-teal-500/10 border border-teal-500/20 text-teal-400">
                <Filter size={9} />{p["ftgd-dns"]?.filters?.length ?? 0}
            </span>
        ),
    },
    {
        key: "_blockCount", label: "Block Rules", width: "w-20",
        render: p => {
            const count = p["ftgd-dns"]?.filters?.filter(f => f.action === "block").length ?? 0;
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500/10 border border-red-500/20 text-red-400">
                    {count}
                </span>
            );
        },
    },
    {
        key: "_monitorCount", label: "Monitor Rules", width: "w-24",
        render: p => {
            const count = p["ftgd-dns"]?.filters?.filter(f => f.action === "monitor").length ?? 0;
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                    {count}
                </span>
            );
        },
    },
    { key: "block-action", label: "Block Action", width: "w-24", render: p => <ValChip val={p["block-action"]} /> },
    { key: "block-botnet", label: "Block Botnet", width: "w-24", render: p => <ValChip val={p["block-botnet"]} /> },
    { key: "safe-search", label: "Safe Search", width: "w-24", render: p => <ValChip val={p["safe-search"]} /> },
    { key: "youtube-restrict", label: "YouTube", width: "w-20", render: p => <ValChip val={p["youtube-restrict"]} /> },
    { key: "strip-ech", label: "Strip ECH", width: "w-20", render: p => <ValChip val={p["strip-ech"]} /> },
    { key: "log-all-domain", label: "Log All Domain", width: "w-24", render: p => <ValChip val={p["log-all-domain"]} /> },
    { key: "sdns-domain-log", label: "SDNS Domain Log", width: "w-28", render: p => <ValChip val={p["sdns-domain-log"]} /> },
    { key: "sdns-ftgd-err-log", label: "SDNS Err Log", width: "w-24", render: p => <ValChip val={p["sdns-ftgd-err-log"]} /> },
    { key: "redirect-portal", label: "Redirect Portal", width: "w-28", render: p => <ValChip val={p["redirect-portal"]} /> },
    {
        key: "comment", label: "Comment", width: "min-w-[140px]",
        render: p => <p className="text-[10px] text-gray-200 truncate max-w-[180px]" title={p.comment}>{p.comment || "—"}</p>,
    },
];

// ── Main Component ────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 20, 50];

export default function DnsFilterProfiles({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [profiles, setProfiles] = useState<DnsProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "name", dir: "asc" });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "dns_filter_profiles",
                    }),
                    limit: 1,
                },
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setProfiles(parsed.items ?? []);
            } else showError("Failed to load DNS filter profiles");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { setPage(1); }, [search]);

    // Filter
    const filtered = profiles.filter(p => {
        if (!search) return true;
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.comment?.toLowerCase().includes(q);
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        let av: any, bv: any;
        if (sort.key === "_filterCount") { av = a["ftgd-dns"]?.filters?.length ?? 0; bv = b["ftgd-dns"]?.filters?.length ?? 0; }
        else if (sort.key === "_blockCount") { av = a["ftgd-dns"]?.filters?.filter(f => f.action === "block").length ?? 0; bv = b["ftgd-dns"]?.filters?.filter(f => f.action === "block").length ?? 0; }
        else if (sort.key === "_monitorCount") { av = a["ftgd-dns"]?.filters?.filter(f => f.action === "monitor").length ?? 0; bv = b["ftgd-dns"]?.filters?.filter(f => f.action === "monitor").length ?? 0; }
        else { av = (a as any)[sort.key]; bv = (b as any)[sort.key]; }
        const cmp = typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av ?? "").localeCompare(String(bv ?? ""));
        return sort.dir === "asc" ? cmp : -cmp;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageItems = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);
    const start = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, sorted.length);

    const handleSort = (key: string) => {
        setSort(prev => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
        setPage(1);
    };

    return (
        <div className="flex flex-col gap-3">

            {/* Filters Bar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative group">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-200 group-focus-within:text-teal-400 transition-colors" />
                    <input
                        type="text"
                        value={search}
                        onChange={e => { setSearch(e.target.value); }}
                        placeholder="Search profile name..."
                        className="w-56 bg-gray-900 border border-gray-700 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-teal-500/50 transition-all"
                    />
                </div>
                {search && (
                    <button onClick={() => setSearch("")}
                        className="text-[9px] text-teal-400 hover:text-teal-300 font-bold uppercase tracking-widest">
                        Clear
                    </button>
                )}
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="ml-auto p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-teal-400"} />
                </button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-teal-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">Loading DNS filter profiles...</span>
                </div>
            ) : sorted.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 opacity-40">
                    <Shield size={22} className="text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No profiles found</span>
                </div>
            ) : (
                <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/60">
                                    {COLS.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleSort(col.key)}
                                            className={`px-3 py-2 text-[9px] font-bold text-gray-200 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${col.width}`}
                                        >
                                            <div className="flex items-center gap-1">
                                                {col.label}
                                                <SortIcon col={col.key} sort={sort} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {pageItems.map(profile => (
                                    <tr
                                        key={profile.name}
                                        className="hover:bg-gray-700/20 transition-colors"
                                    >
                                        {COLS.map(col => (
                                            <td key={col.key} className="px-3 py-2.5 align-middle">
                                                {col.render(profile)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-700/60 bg-gray-800/40 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] text-gray-200 uppercase tracking-widest">Rows</span>
                                <select
                                    value={pageSize}
                                    onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="bg-gray-900 border border-gray-700 rounded px-1.5 py-0.5 text-[10px] text-gray-300 focus:outline-none"
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </div>
                            <span className="text-[9px] text-gray-200">
                                <span className="text-gray-300">{start}</span>{" – "}<span className="text-gray-300">{end}</span>
                                <span className="mx-1 text-gray-200">/</span>
                                <span className="text-gray-300 font-bold">{sorted.length}</span>
                                {sorted.length !== profiles.length && (
                                    <span className="text-gray-200 ml-1">(filtered from {profiles.length})</span>
                                )}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button disabled={safePage === 1} onClick={() => setPage(1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsLeft size={13} />
                            </button>
                            <button disabled={safePage === 1} onClick={() => setPage(p => p - 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronLeft size={13} />
                            </button>
                            <span className="px-2 py-0.5 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded text-[10px] font-bold mx-1 min-w-[28px] text-center">
                                {safePage}
                            </span>
                            <span className="text-[9px] text-gray-200">/ {totalPages}</span>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronRight size={13} />
                            </button>
                            <button disabled={safePage >= totalPages} onClick={() => setPage(totalPages)}
                                className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded transition-all">
                                <ChevronsRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
